import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Draggable from 'react-draggable';
import classNames from '../../utils/dom-utils';
import Timeline from './components/Timeline';
import pluck from '../../utils/array-utils';
import { getSpreadNoOfImageUrlsFromList } from '../utils/video-utils';
import ArrowLeft from '../../Icons/ArrowLeft';
import ArrowRight from '../../Icons/ArrowRight';
import LoadingGif from '../assets/loading.gif';
import Loading from '../../Icons/Loading';
import './scss/index.scss';

const translate = (key) => (window.translate ? window.translate(key) : key);

class FramePicker extends Component {
  constructor(props) {
    super(props);
    this.imagesList = [];
    this.allocatedUrls = [];

    this.draggableRef = React.createRef();
    this.isDragging = false;

    this.state = {
      xPos: 0,
      videoPos: props.videoPosition,
      showCanvas: false,
      currentImage: null,
      hideRightArrow: false,
      hideLeftArrow: true,
      imagesListData: Array(10).fill({ objectUrl: LoadingGif }),
      videoDuration: 0,
      metadataLoaded: false,
      canvasInit: false,
      isSeeking: false,
      framePickerOffsetWidth: 0,
      isFrameDecodeUnsupported: false,
    };
  }

  componentDidMount() {
    this.canvasContext = this.canvasElement.getContext("2d", {
      alpha: false,
      desynchronized: true,
    });
    this.setState({
      framePickerOffsetWidth: this.framePickerElement
        ? this.framePickerElement.offsetWidth
        : 0,
    });
    window.addEventListener("resize", this.updateDimensions);
  }

  componentDidUpdate(prevProps, prevState) {
    const { videoPosition } = this.props;
    const { framePickerOffsetWidth, videoDuration } = this.state;

    if (this.isDragging) return;

    const videoPosChanged = videoPosition !== prevProps.videoPosition;
    const widthChanged =
      framePickerOffsetWidth !== (this.framePickerElement?.offsetWidth || 0);
    const durationLoaded = videoDuration > 0 && prevState.videoDuration === 0;

    if (videoPosChanged || widthChanged || durationLoaded) {
      const vid = this.sliderVideoElement;
      const picker = this.framePickerElement;
      if (!vid || !picker || !videoDuration) return;

      const posOffset = videoPosition / videoDuration;
      const offsetWidthVal = picker.offsetWidth - vid.offsetWidth;
      const sliderPos = Math.max(
        0,
        Math.min(posOffset * offsetWidthVal, offsetWidthVal),
      );

      this.setState({
        xPos: sliderPos,
        videoPos: videoPosition,
        framePickerOffsetWidth: picker.offsetWidth,
      });

      if (vid.currentTime !== videoPosition) {
        vid.currentTime = videoPosition;
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.updateDimensions);
    this.allocatedUrls.forEach((url) => URL.revokeObjectURL(url));
  }

  onLoadedMetadata = () => {
    const trimLastMs = 0.1;
    const duration =
      parseFloat(this.videoElement.duration.toFixed(4)) - trimLastMs;
    this.setState({
      videoDuration: duration,
      metadataLoaded: true,
    });
  };

  onSliderSeeked = () => {
    const { isSeeking, videoDuration, videoPos } = this.state;
    let sliderPos = 0;

    if (
      videoPos !== 0 &&
      videoDuration &&
      this.sliderVideoElement &&
      this.framePickerElement
    ) {
      const posOffset = videoPos / videoDuration;
      const offsetWidthVal =
        this.framePickerElement.offsetWidth -
        this.sliderVideoElement.offsetWidth;
      sliderPos = posOffset * offsetWidthVal;
    }
    if (isSeeking) {
      this.setState({ isSeeking: false, xPos: sliderPos });
    }
  };

  updateDimensions = () => {
    if (!this.framePickerElement) return;
    this.setState(
      {
        isSeeking: true,
        framePickerOffsetWidth: this.framePickerElement.offsetWidth,
      },
      () => {
        this.onSliderSeeked();
      },
    );
  };

  onVideoError = () => {
    this.setState({ isFrameDecodeUnsupported: true });
  };

initCanvas = () => {
    if (!this.videoElement || !this.canvasElement) return;

    // A video track the browser can't decode (e.g. HEVC on Linux Chromium)
    // can still reach HAVE_CURRENT_DATA via its audio track, but there is
    // no frame to capture, so every createImageBitmap call would fail.
    if (!this.videoElement.videoWidth || !this.videoElement.videoHeight) {
      this.setState({ isFrameDecodeUnsupported: true });
      return;
    }

    const nativeWidth = this.videoElement.videoWidth;
    const nativeHeight = this.videoElement.videoHeight;

    // Thumbnails render at a few dozen px in the strip — sampling a
    // 1080p/4K source into a full-resolution canvas was the largest
    // cost in the capture loop. Cap the long edge instead.
    const maxEdge = 320;
    const scale = Math.min(1, maxEdge / Math.max(nativeWidth, nativeHeight));

    this.canvasElement.width = Math.max(2, Math.round(nativeWidth * scale));
    this.canvasElement.height = Math.max(2, Math.round(nativeHeight * scale));

    this.setState({ canvasInit: true }, () => {
      this.generateTimelineFrames();
    });
  };

generateTimelineFrames = async () => {
    const { videoDuration } = this.state;
    if (!videoDuration || !this.videoElement) return;

    const totalFrames = 10;
    const segmentDuration = videoDuration / totalFrames;

    for (let i = 0; i < totalFrames; i++) {
      if (!this.videoElement) break;

      const targetTime = i * segmentDuration + segmentDuration / 2;
      const safeTargetTime = Math.min(targetTime, videoDuration);

      const priorTime = this.videoElement.currentTime;
      this.videoElement.currentTime = safeTargetTime;

      const frameReady = await new Promise((resolve) => {
        if (!this.videoElement) {
          resolve(false);
          return;
        }

        // If currentTime was already at this target, no `seeking` state
        // change ever happens and the video may already satisfy the ready
        // check below on the very first tick — that check would then be
        // describing the frame we just left, not the one we asked for.
        // Only trust an immediate "ready" if we actually asked to move.
        const sameTime = Math.abs(priorTime - safeTargetTime) < 0.01;

        const startTime = Date.now();
        const checkReady = () => {
          if (!this.videoElement) {
            resolve(false);
            return;
          }

          const settled =
            !this.videoElement.seeking && this.videoElement.readyState >= 2;

          if (settled && !(sameTime && Date.now() - startTime < 20)) {
            resolve(true);
          } else if (Date.now() - startTime > 2000) {
            resolve(this.videoElement.readyState >= 2);
          } else {
            setTimeout(checkReady, 40);
          }
        };
        checkReady();
      });

      if (!frameReady || !this.videoElement) continue;

      await new Promise((resolve) =>
        requestAnimationFrame(() => requestAnimationFrame(resolve)),
      );

      if (!this.videoElement) break;

      let frameBitmap = null;
      try {
        if (this.videoElement.readyState >= 2 && this.videoElement.videoWidth > 0) {
          frameBitmap = await createImageBitmap(this.videoElement);
        }
      } catch (error) {
        console.warn(
          `createImageBitmap failed at frame ${i}, diverting to fallback processing:`,
          error,
        );
      }

      if (frameBitmap) {
        this.processFrameBitmapAsync(frameBitmap, i, safeTargetTime);
      } else {
        this.processFrameFallback(i, safeTargetTime);
      }
    }
  };

  processFrameBitmapAsync = (bitmap, index, timestamp) => {
    if (!this.canvasElement || !this.canvasContext) {
      bitmap.close();
      return;
    }

    this.executeDrawImageOnCanvas(bitmap);
    bitmap.close();

    this.canvasElement.toBlob(
      (blob) => {
        if (blob) {
          const objectUrl = URL.createObjectURL(blob);
          this.allocatedUrls.push(objectUrl);

          this.setState((prevState) => {
            const imagesListData = [...prevState.imagesListData];
            const wasShowingPlaceholder =
              prevState.currentImage === imagesListData[index]?.objectUrl;
            imagesListData[index] = { objectUrl, timeOnVideo: timestamp };
            return {
              imagesListData,
              currentImage: wasShowingPlaceholder
                ? objectUrl
                : prevState.currentImage,
            };
          });
        }
      },
      "image/jpeg",
      0.35,
    );
  };

  processFrameFallback = (index, timestamp) => {
    if (!this.canvasElement || !this.canvasContext || !this.videoElement)
      return;

    try {
      this.executeDrawImageOnCanvas(this.videoElement);

      this.canvasElement.toBlob(
        (blob) => {
          if (blob) {
            const objectUrl = URL.createObjectURL(blob);
            this.allocatedUrls.push(objectUrl);

            this.setState((prevState) => {
              const imagesListData = [...prevState.imagesListData];
              const wasShowingPlaceholder =
                prevState.currentImage === imagesListData[index]?.objectUrl;
              imagesListData[index] = { objectUrl, timeOnVideo: timestamp };
              return {
                imagesListData,
                currentImage: wasShowingPlaceholder
                  ? objectUrl
                  : prevState.currentImage,
              };
            });
          }
        },
        "image/jpeg",
        0.35,
      );
    } catch (fallbackError) {
      console.error(
        "Critical fallback composition limit exceeded:",
        fallbackError,
      );
    }
  };

  executeDrawImageOnCanvas(source) {
    if (!this.canvasElement || !this.canvasContext || !source) return;

    const { rotationFix } = this.props;

    if (Utils.isFirefox && rotationFix) {
      this.canvasContext.save();
      this.canvasContext.beginPath();
      const rotateValue = (-rotationFix * Math.PI) / 180;

      this.canvasContext.translate(
        this.canvasElement.width / 2,
        this.canvasElement.height / 2,
      );
      this.canvasContext.rotate(rotateValue);

      const isSidelong = rotationFix === 90 || rotationFix === 270;
      const drawWidth = isSidelong
        ? this.canvasElement.height
        : this.canvasElement.width;
      const drawHeight = isSidelong
        ? this.canvasElement.width
        : this.canvasElement.height;

      this.canvasContext.drawImage(
        source,
        -drawWidth / 2,
        -drawHeight / 2,
        drawWidth,
        drawHeight,
      );
      this.canvasContext.restore();
    } else {
      this.canvasContext.drawImage(
        source,
        0,
        0,
        this.canvasElement.width,
        this.canvasElement.height,
      );
    }
  }

  updateXPosState(xPosition) {
    if (
      !this.state.metadataLoaded ||
      !this.sliderVideoElement ||
      !this.framePickerElement
    )
      return;
    let calcPos =
      xPosition -
      this.framePickerElement.offsetLeft -
      this.sliderVideoElement.offsetWidth / 2;

    const maxOffset =
      this.framePickerElement.offsetWidth - this.sliderVideoElement.offsetWidth;
    calcPos = Math.max(0, Math.min(calcPos, maxOffset));
    this.updateXPosition(calcPos, true);
  }

  updateXPosition(sliderPos, fromClick = true) {
    const { videoDuration, metadataLoaded, currentImage, imagesListData } =
      this.state;
    if (!metadataLoaded || !this.sliderVideoElement || !this.framePickerElement)
      return;

    let safeVideoDuration = Math.max(0, videoDuration || 0);
    let safeSliderPos = Math.max(0, sliderPos || 0);

    const offsetWidthVal =
      this.framePickerElement.offsetWidth - this.sliderVideoElement.offsetWidth;
    const safeOffsetWidthVal = Math.max(0, offsetWidthVal || 0);

    if (safeOffsetWidthVal > 0) {
      safeSliderPos = Math.max(0, Math.min(safeSliderPos, safeOffsetWidthVal));
    } else {
      safeSliderPos = 0;
    }

    const remVal = 16;
    const hideLeftArrow = safeOffsetWidthVal <= 0 || safeSliderPos < remVal;
    const hideRightArrow =
      safeOffsetWidthVal <= 0 || safeSliderPos >= safeOffsetWidthVal - remVal;

    let positionOffset =
      safeOffsetWidthVal > 0 ? safeSliderPos / safeOffsetWidthVal : 0;
    positionOffset = Math.max(0, Math.min(positionOffset, 1));

    let updatedImage = currentImage;
    if (imagesListData.length > 0) {
      let imagePos = Math.min(
        Math.round(positionOffset * 9),
        imagesListData.length - 1,
      );
      const image = imagesListData[imagePos];
      if (image && image.objectUrl) updatedImage = image.objectUrl;
    }

    const nextVideoPos = safeVideoDuration * positionOffset;

    if (fromClick && this.sliderVideoElement) {
      this.sliderVideoElement.currentTime = nextVideoPos;
    }

    this.setState({
      xPos: safeSliderPos,
      videoPos: nextVideoPos,
      showCanvas: !fromClick,
      currentImage: updatedImage,
      hideRightArrow,
      hideLeftArrow,
      isSeeking: fromClick,
    });

    if (fromClick) {
      this.props.onSliderChanged(nextVideoPos);
    }
  }

  calculateTimelineImages(NoImages, imagesListData) {
    return getSpreadNoOfImageUrlsFromList(
      NoImages,
      pluck(imagesListData, "objectUrl"),
    );
  }

  render() {
    const { src, aspectRatio } = this.props;
    const {
      currentImage,
      hideRightArrow,
      hideLeftArrow,
      showCanvas,
      videoPos,
      xPos,
      imagesListData,
      isSeeking,
      isFrameDecodeUnsupported,
    } = this.state;
    const timeLineImageUrls = this.calculateTimelineImages(10, imagesListData);

    const displayTime = new Date(videoPos * 1000)
      .toISOString()
      .substr(11, 8)
      .replace(/^(?:00:)?0?/, "");
    const classAspect =
      aspectRatio === 1 ? "square" : aspectRatio > 1 ? "portrait" : "landscape";
    const rootFramePickerClass = classNames(
      "frame-picker",
      `frame-picker--${classAspect}`,
    );

    return (
      <div
        className={rootFramePickerClass}
        ref={(el) => {
          this.framePickerElement = el;
        }}
      >
        {isFrameDecodeUnsupported ? (
          <p
            className="frame-picker__unsupported"
            data-testid="frame-picker-unsupported"
          >
            {translate('video_frame_preview_not_supported')}
          </p>
        ) : (
          <>
            <Timeline
              onTimelineClick={this.updateXPosState.bind(this)}
              timeLineImageUrls={timeLineImageUrls}
            />
            <Draggable
              axis="x"
              nodeRef={this.draggableRef}
              handle=".video_thumbnail__draggable"
              position={{ x: xPos, y: 0 }}
              bounds="parent"
              allowAnyClick
              defaultClassNameDragging="grabbing"
              onStart={() => {
                this.isDragging = true;
              }}
              onDrag={(e, data) => this.updateXPosition(data.x, false)}
              onStop={() => {
                this.isDragging = false;
                this.setState({ showCanvas: false, isSeeking: true });

                if (this.sliderVideoElement) {
                  this.sliderVideoElement.currentTime = this.state.videoPos;
                }
                this.props.onSliderChanged(this.state.videoPos);
              }}
            >
              <div ref={this.draggableRef} className="video_thumbnail__draggable">
                <span className="video-time">{displayTime}</span>
                {!hideLeftArrow && <ArrowLeft className="svg-chevron-left" />}
                <video
                  className="draggable-video"
                  muted
                  ref={(el) => {
                    this.sliderVideoElement = el;
                  }}
                  src={src}
                  crossOrigin="true"
                  onSeeked={this.onSliderSeeked}
                />
                {showCanvas && (
                  <div className="draggable-video image-overlay">
                    <img alt="" src={currentImage} draggable="false" />
                  </div>
                )}
                {isSeeking && <Loading />}
                {!hideRightArrow && <ArrowRight className="svg-chevron-right" />}
              </div>
            </Draggable>
          </>
        )}

        {/* Render Layer Vault: Isolated display properties to keep graphic render paths working */}
        <div
          style={{
            position: "absolute",
            width: "0px",
            height: "0px",
            overflow: "hidden",
            pointerEvents: "none",
            zIndex: -1,
          }}
        >
          <video
            data-testid="frame-picker-source-video"
            muted
            ref={(el) => {
              this.videoElement = el;
            }}
            src={src}
            onLoadedData={this.initCanvas}
            onLoadedMetadata={this.onLoadedMetadata}
            onError={this.onVideoError}
            crossOrigin="true"
            style={{ width: "320px", height: "180px" }}
          />
        </div>
        <canvas
          ref={(el) => {
            this.canvasElement = el;
          }}
          style={{ display: "none" }}
        />
      </div>
    );
  }
}

FramePicker.defaultProps = {
  rotationFix: 0,
};

FramePicker.propTypes = {
  src: PropTypes.string.isRequired,
  onSliderChanged: PropTypes.func.isRequired,
  videoPosition: PropTypes.number.isRequired,
  aspectRatio: PropTypes.number.isRequired,
  rotationFix: PropTypes.number,
};

export default FramePicker;
